import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { css } from '@emotion/react';
import CloseSVG from '@birdi/icons/close.svg';
import WorkspaceGridSVG from '@birdi/icons/workspace-grid.svg';
import { closeButtonCSS, contentCSS, portalCSS } from '@birdi/modal-kit';
import { useForm, Controller } from 'react-hook-form';
import { buttonBlue, buttonGrey, forms, blocks } from '@birdi/theme';
import {
  stroot,
  auth,
  organisation as organisationAPI,
  user,
} from '@birdi/js-sdk';
import { useMetaSelector } from '../meta/reducer';

interface AddWorkspaceModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  switchToWS?: boolean;
}

export const AddWorkspaceModal: React.FC<AddWorkspaceModalProps> = ({
  open,
  setOpen,
  switchToWS = true,
}) => {
  const meta = useMetaSelector();
  const [maxWorkspacesReached, setMaxWorkspacesReached] = useState(false);
  const { control, formState, clearErrors, handleSubmit, getValues } =
    useForm();

  useEffect(() => {
    if (open) {
      organisationAPI.getWorkspaces(stroot('xon5oo')).then((res) => {
        setMaxWorkspacesReached(res?.body?.isOverLimit || false);
      });
    }
  }, [open]);

  const confirm = async () => {
    const values = getValues();
    const newWorkspace = await organisationAPI.addWorkspace(
      stroot('ca0ohc'),
      values,
    );
    const scopes = await user.getScopes(stroot('ior5ah'));
    const newScope = scopes.body.items.filter(
      (scope) => scope.organisationId === newWorkspace.body.id,
    );
    const newScopeId = newScope[0].id;
    setOpen(false);
    if (switchToWS) {
      auth
        .switchContext(stroot('aigee1'), {
          scopeId: newScopeId,
        })
        .then(() => (window.location = '/'));
    } else {
      window.location.reload();
    }
  };
  const cancel = () => {
    setOpen(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />

        <div css={portalCSS}>
          <Dialog.Content
            css={(theme) => css`
              ${contentCSS(theme)};
              overflow-y: auto;
            `}
          >
            <Dialog.Close css={closeButtonCSS}>
              <CloseSVG />
            </Dialog.Close>
            <div
              css={css`
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 0.5em;
                font-size: 1.25em;
                font-weight: bold;
              `}
            >
              <WorkspaceGridSVG />
              <span>Create a workspace</span>
            </div>
            <hr />
            <p>
              Workspaces help you manage data and collaboration for each client.
              <br />
              Give this workspace a name to get started - you can invite your
              team next.
            </p>
            <form css={blocks.formStyles} onSubmit={handleSubmit(confirm)}>
              <label
                css={forms.blockLabel}
                htmlFor="workspaceName"
                id="add-workspace-name"
              >
                Workspace name
                <Controller
                  render={({ field: { onChange } }) => (
                    <input
                      css={forms.textInput}
                      type="text"
                      id="workspaceName"
                      disabled={maxWorkspacesReached}
                      placeholder="e.g. Acme Mining, City of Melbourne, Coastal Rail Project"
                      onChange={(event) => {
                        onChange(event.target.value);
                        clearErrors('workspaceName');
                      }}
                    />
                  )}
                  control={control}
                  name="workspaceName"
                  rules={{ required: true }}
                />
                {formState.errors.workspaceName && (
                  <span css={forms.errorMessage}>
                    Workspace name is required.
                  </span>
                )}
              </label>
              <div
                css={css`
                  display: flex;
                  flex-direction: row;
                  align-items: center;
                  justify-content: center;
                  margin: 2em 0 0 0;
                  gap: 0.5rem;
                `}
              >
                <button
                  type="button"
                  css={(theme) => css`
                    ${buttonGrey(theme)}
                    height: 2.5rem;
                    width: 7rem;
                    background: ${theme.mono0};
                    color: ${theme.deepOcean};
                    border: solid 1px ${theme.deepOcean};
                  `}
                  onClick={cancel}
                >
                  <span>Cancel</span>
                </button>
                <button
                  type="submit"
                  css={(theme) => css`
                    ${buttonBlue(theme)}
                    height: 2.5rem;
                    min-width: 7rem;
                  `}
                  disabled={maxWorkspacesReached}
                >
                  <span>Create workspace</span>
                </button>
              </div>
              {maxWorkspacesReached && (
                <div
                  css={(theme) => css`
                    margin-top: 1.5em;
                    color: ${theme.crimsonSunbird};
                  `}
                >
                  You've reached the maximum number of workspaces for your plan.{' '}
                  <a css={blocks.linkPrimary} href="/billing/options">
                    {' '}
                    Upgrade your plan{' '}
                  </a>{' '}
                  to add more.
                </div>
              )}
              <div
                css={css`
                  margin-top: 1.5em;
                `}
              >
                If you're having issues, contact support via chat from the
                dashboard or email{' '}
                <a css={blocks.linkPrimary} href="mailto:support@birdi.io">
                  support@birdi.io
                </a>
                .
              </div>
            </form>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
